<template>
  <div class="QuizModule">
    <div class="slide">
      <div class="body">
        <div class="slideContentContainer">
          <component v-for="slide, index in content.slides"
            :key="slide._key"
            :is="slideContentComponents[slide._type]"
            :content="slide"
            :isCurrentSlide="index === currentSlideIndex"
            v-bind="{ selectedOptions, fieldConfig, fieldValues, fieldErrors }"
            class="slideContent"
            :class="getSlidePosition(index)"
            @next="gotoNextSlide"
            @selectOption="doSelectOption"
            @updateField="doUpdateField"
          />
          <SubmittedSlide
            v-if="content.submittedSlide"
            :content="content.submittedSlide"
            class="slideContent"
            :class="submissionStatus === 'submission-confirmed' ? 'current' : 'next'"
          />
        </div>
        <SlideFooter v-if="submissionStatus !== 'submission-confirmed'"
          :content="currentSlide"
          :isStartingSlide="currentSlideIndex === 0"
          v-bind="{ progress }"
          class="footer"
          @next="doValidateThenSubmitOrNext"
          @back="gotoPreviousSlide"
        />
      </div>
      <div class="sidepanes">
        <div v-for="slide, index in content.slides"
          :key="slide._key"
          class="sidepaneWrapper"
          :class="getSlidePosition(index)"
        >
          <Sidepane v-if="slide.sidepane"
            :content="slide.sidepane"
          />
        </div>
        <div class="sidepaneWrapper"
          :class="submissionStatus === 'submission-confirmed' ? 'current' : 'next'"
        >
          <Sidepane v-if="content.submittedSlide?.sidepane"
            :content="content.submittedSlide.sidepane"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import submitForm from '~/lib/submitForm.js'
import getCountryData from '~/lib/countryData.js'
import getFieldError from '~/lib/getFieldError.js'

import DetailsSlide from './slideContent/DetailsSlide.vue'
import InformationalSlide from './slideContent/InformationalSlide.vue'
import MatchSlide from './slideContent/MatchSlide.vue'
import MultichoiceSlide from './slideContent/MultichoiceSlide.vue'
import MultilinkSlide from './slideContent/MultilinkSlide.vue'
import SubmittedSlide from './slideContent/SubmittedSlide.vue'

import Sidepane from './elems/Sidepane.vue'
import SlideFooter from './elems/SlideFooter.vue'

const slideContentComponents = {
  detailsSlide: 'DetailsSlide',
  informationalSlide: 'InformationalSlide',
  matchSlide: 'MatchSlide',
  multichoiceSlide: 'MultichoiceSlide',
  multilinkSlide: 'MultilinkSlide',
  submittedSlide: 'SubmittedSlide',
}

const fieldConfig = {
  firstName: {
    component: 'TextInput',
    required: true
  },
  lastName: {
    component: 'TextInput',
    required: true
  },
  company: {
    component: 'TextInput',
    required: true
  },
  jobTitle: {
    component: 'TextInput',
    required: true
  },
  email: {
    component: 'TextInput',
    required: true
  },
  phone: {
    component: 'TextInput',
    required: true
  },
  comments: {
    component: 'TextAreaInput',
  },
}

function formatOptionsForComments(options) {
  const strings = Object.entries(options).map(
    ([key, value]) => `"${value}" (${key.replaceAll('-', ' ')})`
  )
  return `Quiz responses: ${strings.join(', ')}`
}

export default {
  components: {
    InformationalSlide,
    DetailsSlide,
    MatchSlide,
    MultichoiceSlide,
    MultilinkSlide,
    Sidepane,
    SlideFooter,
    SubmittedSlide,
  },
  props: {
    content: Object,
  },
  provide() {
    return {
      locale: this.content.locale,
    }
  },
  data() {
    return {
      selectedOptions: {},
      fieldConfig,
      fieldValues: {},
      fieldErrors: {},
      currentSlideIndex: 0,
      slideContentComponents,
      submissionStatus: 'not-submitted',
    }
  },
  computed: {
    currentSlide() {
      return (
        this.submissionStatus === 'submission-confirmed'
          ? this.content.submittedSlide
          : this.content.slides?.[this.currentSlideIndex]
      )
    },
    slidesEnabled() {
      return this.content.slides.map(
        (slideContent) => {
          if (slideContent.condition) {
            // multichoiceSlide/informationalSlide
            const { condition } = slideContent
            const values = condition.optionValue.split('|').map((value) => value.trim())
            return values.includes(this.selectedOptions[condition.optionKey])
          }
          if (slideContent.optionKey && slideContent.variations) {
            // matchSlide
            const { optionKey, variations, defaultBody } = slideContent
            const values = variations.map((variation) => variation.optionValue)
            return Boolean(defaultBody) || values.includes(this.selectedOptions[optionKey])
          }
          return true
        }
      )
    },
    progress() {
      const count = this.slidesEnabled.slice(0, this.currentSlideIndex).filter(Boolean).length
      const total = this.slidesEnabled.filter(Boolean).length
      return (count + 1) / total
    },
  },
  methods: {
    getSlidePosition(index) {
      if (this.submissionStatus === 'submission-confirmed') {
        return 'previous'
      }
      switch (Math.sign(index - this.currentSlideIndex)) {
      case -1:
        return 'previous'
      case 0:
        return 'current'
      case +1:
        return 'next'
      }
    },
    gotoNextSlide() {
      this.pushEvent('INS_quiz_module_next_slide')
      this.currentSlideIndex = this.slidesEnabled.findIndex(
        (isEnabled, index) => isEnabled && index > this.currentSlideIndex
      )
    },
    gotoPreviousSlide() {
      this.pushEvent('INS_quiz_module_previous_slide')
      this.currentSlideIndex = this.slidesEnabled.findLastIndex(
        (isEnabled, index) => isEnabled && index < this.currentSlideIndex
      )
    },
    doValidateThenSubmitOrNext() {
      if (this.currentSlide.fields) {
        var allValid = true
        for (const { fieldName } of this.currentSlide.fields) {
          const error = getFieldError(fieldName, this.fieldValues[fieldName], fieldConfig[fieldName])
          this.fieldErrors[fieldName] = error
          if (error) {
            allValid = false
          }
        }
        if (allValid) {
          if (this.currentSlide.isSubmissionSlide) {
            this.submitLead()
          }
          else {
            this.gotoNextSlide()
          }
        }
      }
      else {
        this.gotoNextSlide()
      }
    },
    doSelectOption(key, value) {
      if (key) {
        this.selectedOptions[key] = value
      }
      this.gotoNextSlide()
    },
    doUpdateField(name, value, inputType) {
      this.fieldValues[name] = value
      const error = getFieldError(name, value, fieldConfig[name])
      switch (inputType) {
      case 'input':
        if (!error) {
          delete this.fieldErrors[name]
        }
        break;
      case 'change':
        this.fieldErrors[name] = error
        break;
      }
    },
    pushEvent(eventName) {
      const event = {
        event: eventName,
        heading: this.content.slides?.[0].title,
        brand: 'inseego',
        flow: 'sales',
        step: this.content.slides?.[this.currentSlideIndex].title,
      }
      window.dataLayer.push(event)
    },
    submitLead() {
      const { locale, digitalLeadType } = this.content
      const countryData = getCountryData(locale)
      const submissionData = {
        ...this.fieldValues,
        digitalLeadType: digitalLeadType ?? 'Quiz lead form',
        incentive: this.content.slides[0].title,
        country: countryData?.countryName,
        comments: formatOptionsForComments(this.selectedOptions),
        requestType: 'sales',
      }
      if (this.selectedOptions['connection-problem']) {}
      this.submissionStatus = 'awaiting-confirmation'
      submitForm({ variant: '5g', locale }, submissionData).then(
        (response) => {
          if (response.ok) {
            this.submissionStatus = 'submission-confirmed'
            this.pushEvent('INS_quiz_module_conversion')
          }
          else {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
        }
      )
    },
  },
}
</script>

<style scoped>

.slide {
  background-color: #0018E1;
  overflow-x: hidden;
}

.slideContentContainer {
  display: grid;
}

.slideContent {
  grid-column: 1;
  grid-row: 1;
  position: relative;
  left: 0;
  opacity: 1;
  transition: all 0.5s;
}

.slideContent.previous {
  left: -100%;
  opacity: 0;
}

.slideContent.next {
  left: 100%;
  opacity: 0;
}

.body {
  padding: var(--spacing-11) var(--spacing-11) var(--spacing-8);
}

.footer {
  margin-top: var(--spacing-4);
}

.sidepanes {
  display: none;
}

@media (--screen-lg) {

  .slide {
    display: flex;
    justify-content: space-between;
    border-radius: 10px;
  }

  .body {
    flex-grow: 1;
    max-width: 65%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    --narrow-child-width: 90%;  /* This var used by MultichoiceSlide too */
  }

  .footer {
    width: var(--narrow-child-width);
  }

  .sidepanes {
    z-index: 1;
    display: grid;
    flex-shrink: 0;
    padding: var(--spacing-11) 0;
    justify-items: end;
  }

  .sidepanes > * {
    grid-column: 1;
    grid-row: 1;
  }

  .sidepaneWrapper {
    transition: opacity 0.5s;
  }

  .sidepaneWrapper:not(.current) {
    opacity: 0;
    pointer-events: none;
  }
}
</style>
